import "./MarketMapComponent.scss";
import template from "./MarketMapComponent.html";
import {IComponentOptions, IFilterService, IPostLink, IRootElementService} from "angular";
import {MarketMapService} from "../../service/MarketMapService";
import {MarketMapHierarchyData} from "../../models/MarketMapHeirarchyData";
import {buildChartData} from "../../util/MarketMapUtil";
import {ComponentCallback, TfPercentageFilter} from "../../../../../../../types/AngularJS";
import {ObjectUtil} from "../../../../../../../js/util/ObjectUtil";
import {Consumer} from "../../../../../../../types/GenericTypes";
import {SecurityMarketStateRecord} from "../../models/SecurityMarketStateRecord";
import {QuoteStatus} from "../../../../../../../quote/models/QuoteRecord";
import {NumberSuffixPipe} from "../../../../../pipes/NumberSuffixPipe";

export const MarketMap = "tfMarketMap";

export function marketMap(): IComponentOptions {
    return {
        template,
        bindings: {
            onSecuritySelected: "&"
        },
        controllerAs: "ctrl",
        controller: MarketMapComponentController
    };
}

export class MarketMapComponentController implements IPostLink {

    /** @output */
    public readonly onSecuritySelected: ComponentCallback;

    // These are actually used as a percentage basis, hence being 100
    private readonly width = 100;
    private readonly height = 100;

    private readonly x = d3.scale.linear().domain([0, this.width]).range([0, this.width]);
    private readonly y = d3.scale.linear().domain([0, this.height]).range([0, this.height]);

    private currentDepth = 0;
    private title: any;
    private readonly maxZ = 100;

    private readonly marketCapFilter: NumberSuffixPipe;
    private readonly percentageFilter: TfPercentageFilter;
    private readonly translateFilter: (value: string) => string;

    private readonly data: Promise<MarketMapHierarchyData>;

    /** @ngInject */
    public constructor(private readonly marketMapService: MarketMapService,
                       private readonly $element: IRootElementService,
                       $filter: IFilterService) {
        this.data = this.marketMapService.state().then((state) => buildChartData(state, this.translateFilter));
        this.marketCapFilter = $filter("thousandSuffix");
        this.percentageFilter = $filter("percentage");
        this.translateFilter = $filter("translate");
    }

    public $postLink(): void {
        this.buildChart();
    }

    private async buildChart() {
        const rootNode = this.$element[0];

        const data = await this.data;

        const nodes = d3.hierarchy(data)
                        .sum((d: D3HierarchyDataNode) => d.value)
                        .sort((a: any, b: any) => b.value - a.value);

        this.configureLayout(nodes);

        const moveTooltip = (d: D3HierarchyDataNode) => {
            const nodeData: MarketMapHierarchyData = d.data;
            if (nodeData.state == null) {
                return;
            }

            const eventX = d3.event.pageX;
            const eventY = d3.event.pageY;

            const mouseX = eventX - rootNode.offsetLeft;
            const mouseY = eventY - rootNode.offsetTop;

            const sourceSize = rootNode.getBoundingClientRect();
            const sourceWidth = sourceSize.width;
            const halfWidth = sourceWidth / 2;
            const sourceHeight = sourceSize.height;
            const halfHeight = sourceHeight / 2;

            if (mouseX <= halfWidth) {
                tooltip.style("right", "auto");
                tooltip.style("left", `${mouseX + 15}px`);
            } else {
                tooltip.style("left", "auto");
                tooltip.style("right", `${sourceWidth - mouseX + 15}px`);
            }

            if (mouseY <= halfHeight) {
                tooltip.style("top", `${mouseY + 15}px`);
                tooltip.style("bottom", "auto");
            } else {
                tooltip.style("top", "auto");
                tooltip.style("bottom", `${sourceHeight - mouseY + 15}px`);
            }
        };

        const chart = d3.select(rootNode).select(".chart-section");
        const cells = chart.selectAll(".node")
                           .data(nodes.descendants())
                           .enter()
                           .append("div")
                           .attr("class", "node");

        const text = cells.append("p")
                          .attr("class", "node-title")
                          .text((d: D3HierarchyDataNode) => (d.data.name != null) ? d.data.name : "");

        const zoom = this.zoom(cells, text);

        this.title = d3.select(rootNode)
                       .select(".title-section")
                       .on("click", zoom);

        const tooltip = d3.select(rootNode)
                          .append("div")
                          .classed("market-map-tooltip", true)
                          .style("display", "none");

        const updateTooltip = (d: D3HierarchyDataNode) => {
            const nodeData: MarketMapHierarchyData = d.data;
            if (nodeData.state == null) {
                return;
            }
            const state = nodeData.state;
            const html = this.tooltipTemplate(state);

            tooltip.html(html)
                   .style("display", "block");
        };

        const hideTooltip = () => tooltip.style("display", "none");

        this.updateTitle(nodes);

        this.styleCells(cells);

        cells.classed("has-change", (d: D3HierarchyDataNode) => this.hasQuoteChange(d))
             .style("z-index", (d: D3HierarchyDataNode) => this.nodeDepth(d))
             .classed("change-up", (d: D3HierarchyDataNode) => this.isChangeUp(d))
             .classed("change-down", (d: D3HierarchyDataNode) => this.isChangeDown(d))
             .classed("change-1", (d: D3HierarchyDataNode) => this.isChange1(d))
             .classed("change-2", (d: D3HierarchyDataNode) => this.isChange2(d))
             .classed("change-3", (d: D3HierarchyDataNode) => this.isChange3(d))
             .classed("change-4", (d: D3HierarchyDataNode) => this.isChange4(d))
             .classed("change-5", (d: D3HierarchyDataNode) => this.isChange5(d))
             .on("mouseenter", updateTooltip)
             .on("mousemove", moveTooltip)
             .on("mouseleave", hideTooltip);

        cells.filter((d: D3HierarchyDataNode) => this.isValueNode(d))
             .classed("value-node", true)
             .on("click", (d: D3HierarchyDataNode) => this.securityNodeSelected(d));

        text.classed("hide", (d: D3HierarchyDataNode) => d.depth !== this.currentDepth + 1);

        cells.filter((d: D3HierarchyDataNode) => d.children != null && d.children.length > 0)
             .on("click", zoom);
    }

    private configureLayout(data: MarketMapHierarchyData) {
        d3.treemap()
          .size([this.width, this.height])
          .paddingInner(0)
          .round(false)(data);
    }

    private styleCells(cells: any) {
        cells.classed("main-group", (d: D3HierarchyDataNode) => d.depth === this.currentDepth + 1)
             .classed("group-container", (d: D3HierarchyDataNode) => d.children != null && d.children.length > 0)
             .transition()
             .duration(800)
             .style("left", (d: D3HierarchyDataNode) => `${this.x(d.x0)}%`)
             .style("top", (d: D3HierarchyDataNode) => `${this.y(d.y0)}%`)
             .style("width", (d: D3HierarchyDataNode) => `${this.x(d.x1) - this.x(d.x0)}%`)
             .style("height", (d: D3HierarchyDataNode) => `${this.y(d.y1) - this.y(d.y0)}%`);
    }

    private zoom(cells: any, text: any): Consumer<D3HierarchyDataNode> {
        return (focusNode) => {
            if (focusNode.depth === this.currentDepth) {
                return;
            }
            this.currentDepth = focusNode.depth;
            this.updateTitle(focusNode);

            this.x.domain([focusNode.x0, focusNode.x1]);
            this.y.domain([focusNode.y0, focusNode.y1]);

            this.styleCells(cells);

            cells.filter((d: any) => d.ancestors() != null)
                 .classed("hide", (d: D3HierarchyDataNode) => (d.children != null) ? true : false);

            cells.filter((d: D3HierarchyDataNode) => d.depth > this.currentDepth)
                 .classed("hide", false);

            text.classed("hide", (d: D3HierarchyDataNode) => d.depth !== this.currentDepth + 1);
        };
    }

    private updateTitle(focusNode: D3HierarchyDataNode) {
        const node = focusNode.parent || focusNode;
        this.title
            .datum(node)
            .text(() => {
                let currentNode: D3HierarchyDataNode | null = focusNode;
                const parts: Array<string> = [];
                do {
                    if (currentNode.data.name !== "") {
                        parts.unshift(currentNode.data.name);
                    }
                    currentNode = currentNode.parent;
                } while (currentNode != null);
                return parts.join(" - ");
            });
    }

    private hasQuoteChange(d: D3HierarchyDataNode): boolean {
        const nodeData: MarketMapHierarchyData = d.data;
        const state = nodeData.state;
        return (state != null) &&
            (state.quote.quoteStatus === QuoteStatus.OK) &&
            (state.quote.changePct !== 0);
    }

    private isChangeUp(d: D3HierarchyDataNode): boolean {
        const nodeData: MarketMapHierarchyData = d.data;
        const state = nodeData.state;
        return (state != null) &&
            (state.quote.quoteStatus === QuoteStatus.OK) &&
            (state.quote.changePct > 0);
    }

    private isChangeDown(d: D3HierarchyDataNode): boolean {
        const nodeData: MarketMapHierarchyData = d.data;
        const state = nodeData.state;
        return (state != null) &&
            (state.quote.quoteStatus === QuoteStatus.OK) &&
            (state.quote.changePct < 0);
    }

    private isChange1(d: D3HierarchyDataNode): boolean {
        const nodeData: MarketMapHierarchyData = d.data;
        const state = nodeData.state;

        if (state != null && state.quote.quoteStatus === QuoteStatus.OK) {
            const absChange = Math.abs(state.quote.changePct);

            return absChange > 0 && absChange < 1;
        } else {
            return false;
        }
    }

    private isChange2(d: D3HierarchyDataNode): boolean {
        const nodeData: MarketMapHierarchyData = d.data;
        const state = nodeData.state;

        if (state != null && state.quote.quoteStatus === QuoteStatus.OK) {
            const absChange = Math.abs(state.quote.changePct);

            return absChange >= 0.01 && absChange < 0.02;
        } else {
            return false;
        }
    }

    private isChange3(d: D3HierarchyDataNode): boolean {
        const nodeData: MarketMapHierarchyData = d.data;
        const state = nodeData.state;

        if (state != null && state.quote.quoteStatus === QuoteStatus.OK) {
            const absChange = Math.abs(state.quote.changePct);

            return absChange >= 0.02 && absChange < 0.03;
        } else {
            return false;
        }
    }

    private isChange4(d: D3HierarchyDataNode): boolean {
        const nodeData: MarketMapHierarchyData = d.data;
        const state = nodeData.state;

        if (state != null && state.quote.quoteStatus === QuoteStatus.OK) {
            const absChange = Math.abs(state.quote.changePct);

            return absChange >= 0.03 && absChange < 0.04;
        } else {
            return false;
        }
    }

    private isChange5(d: D3HierarchyDataNode): boolean {
        const nodeData: MarketMapHierarchyData = d.data;
        const state = nodeData.state;

        if (state != null && state.quote.quoteStatus === QuoteStatus.OK) {
            const absChange = Math.abs(state.quote.changePct);

            return absChange >= 0.04;
        } else {
            return false;
        }
    }

    private nodeDepth(d: D3HierarchyDataNode): number {
        if (d.depth === 0) {
            return 0;
        } else {
            return this.maxZ - d.depth;
        }
    }

    private isValueNode(d: D3HierarchyDataNode): boolean {
        const nodeData = d.data as MarketMapHierarchyData;
        return nodeData.state != null;
    }

    private tooltipTemplate(state: SecurityMarketStateRecord): string {
        const marketCap = this.marketCapFilter(state.marketCap, 2);
        const changePct = state.quote.changePct;
        const percentMove = this.percentageFilter(changePct);
        const percentMoveClass = (changePct > 0) ? "up" : (changePct < 0) ? "down" : "";

        return `
                <div class="security-code">
                    <strong>${state.code}</strong>
                </div>
                <div class="company-name">
                    ${state.companyName}
                </div>
                <div class="market-cap">
                    ${this.translateFilter("common.MARKET_CAPITALISATION")}: ${marketCap}
                </div>
                <div class="percentMove">
                    ${this.translateFilter("common.CHANGE")}: <span class="${percentMoveClass}">${percentMove}</span>
                </div>
            `;
    }

    private securityNodeSelected(d: D3HierarchyDataNode) {
        const nodeData = d.data as MarketMapHierarchyData;
        const state = nodeData.state!;
        const security = ObjectUtil.pick(state, "code", "exchange");
        this.onSecuritySelected({security});
    }
}
